import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Filter, Columns as ColumnsIcon } from 'lucide-react';
import type { ColumnDefinition } from '../types';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export interface TableHeaderProps {
  title: string;
  searchValue: string;
  searchPlaceholder: string;
  onSearch: (value: string) => void;
  onFilterToggle: () => void;
  activeFilterCount: number;
  /** Optional slot to render custom actions to the far right */
  rightActions?: React.ReactNode;
  /** Column toggling */
  columns: ColumnDefinition[];
  visibleColumnKeys: string[];
  onToggleColumn: (key: string) => void;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  title,
  searchValue,
  searchPlaceholder,
  onSearch,
  onFilterToggle,
  activeFilterCount,
  rightActions,
  columns,
  visibleColumnKeys,
  onToggleColumn,
}) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 className="text-2xl font-semibold text-sky-900">{title}</h2>
    </div>
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-500/70 h-4 w-4" />
        <Input
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-10 pr-4 h-10 w-72 bg-white border border-gray-200 text-gray-800 placeholder:text-gray-400 rounded-full shadow-sm focus-visible:ring-2 focus-visible:ring-sky-200 focus-visible:border-sky-300"
        />
      </div>
      {rightActions}
      <Button variant="outline" size="sm" className="h-9 bg-white border-gray-200 hover:bg-gray-50" onClick={onFilterToggle}>
        <Filter className="h-4 w-4 mr-2" />
        Filter
        {activeFilterCount > 0 && (
          <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sky-100 px-1 text-xs font-medium text-sky-700">
            {activeFilterCount}
          </span>
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 bg-white border-gray-200 hover:bg-gray-50">
            <ColumnsIcon className="h-4 w-4 mr-2" /> Column
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {columns.map((col) => (
            <DropdownMenuCheckboxItem
              key={col.key}
              checked={visibleColumnKeys.includes(col.key)}
              onCheckedChange={() => onToggleColumn(col.key)}
            >
              {col.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);
